import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import { WordData } from '../../types/types';
import { CrossReference, GardinerSign } from '../../utils/hieroglyphReading';
import { getWordQuiz, getWordReading, getWordTranslation } from '../../utils/localizedContent';
import { displayCategoryLabel } from '../../utils/categoryLabels';
import { getAlphabetSign } from '../../utils/kamaAlphabet';
import CopticText from './CopticText';
import KamaGuideCard, { IbisCheer } from './KamaGuideCard';
import './HieroglyphCard.css';

interface HieroglyphCardProps {
  entryKey: string;
  word: WordData;
  gardinerSigns: GardinerSign[];
  crossReferences: CrossReference[];
  onNavigate?: (reference: CrossReference) => void;
  onAnswered?: (correct: boolean) => void;
  // Resolved `word.signExample` (see resolveSignExample) — fills the
  // « Un mot » tab with a real word containing this sign.
  example?: CrossReference & { word: WordData };
  // Ibis-guided cards only (`word.guide`): the current bubble, driven by
  // KamaLearnMode's Suivant/Précédent. Left out (e.g. a cross-reference
  // detour), KamaGuideCard steps through with its own « Continuer ».
  guideStep?: number;
  onGuideStepChange?: (step: number) => void;
}

type TabId = 'sound' | 'word' | 'origin';

function stripReadingPrefix(reading: string | undefined): string {
  if (!reading) return '';
  return reading.replace(/^(Lecture analytique|Analytical reading)\s*—\s*/, '');
}

// Kama translations follow "Name — gloss" (signs: "Le vautour qui plane… —
// lu comme un a") or "Name – gloss" (vocabulary: "Raisin – Cultivé pour…").
// The stèle shows the name as its title and the gloss as the italic line
// beneath, the way 19d separates "le vautour" from its mnemonic.
function splitTranslation(text: string): [string, string] {
  const match = text.match(/^(.+?)\s+[—–]\s+(.+)$/s);
  return match ? [match[1], match[2]] : [text, ''];
}

// Highlights every occurrence of the sign's transliteration letter inside the
// example word's transliteration (wꜣt → w[ꜣ]t). Egyptological letters are
// precomposed code points (ḥ, š, ẖ…), so a plain split never matches "h"
// inside "ḥ".
function highlightLetter(text: string, letter: string): React.ReactNode {
  if (!letter) return text;
  const parts = text.split(letter);
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {part}
      {i < parts.length - 1 && <span className="is-hl">{letter}</span>}
    </React.Fragment>
  ));
}

// Monument photo (word.photo) with its mandatory licence credit. Tapping the
// image opens it full screen through a portal: the card sits inside
// react-pageflip's transformed pages, where position:fixed would be trapped.
// Noto Sans Egyptian Hieroglyphs draws every sign inside a 1em quadrat that
// sits on the baseline (ink from 0 to ~0.96em), already centered in the line
// box — but flat signs (𓆑 viper, 𓈙 pool, 𓊪 stool) are drawn at the *bottom*
// of that quadrat, as they'd be stacked in real hieroglyphic writing. Shown
// alone in a quiz option they sat on the button's floor. No CSS can center
// each sign's ink, so it's measured (canvas actualBoundingBox*, after the
// web font has loaded) and shifted so its middle lands on the quadrat's.
const GLYPH_OPTION_FONT = "'Noto Sans Egyptian Hieroglyphs', 'Segoe UI Historic', sans-serif";
const GLYPH_OPTION_SIZE = 32; // keep in sync with .hgw__quiz-option--glyphs
let measureCanvas: HTMLCanvasElement | null = null;
const CenteredGlyphs: React.FC<{ text: string }> = ({ text }) => {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const font = `${GLYPH_OPTION_SIZE}px ${GLYPH_OPTION_FONT}`;
    const measure = () => {
      if (cancelled) return;
      measureCanvas ??= document.createElement('canvas');
      const ctx = measureCanvas.getContext('2d');
      if (!ctx) return;
      ctx.font = font;
      const m = ctx.measureText(text);
      const inkMiddle = (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
      setOffset(inkMiddle - GLYPH_OPTION_SIZE / 2);
    };
    // Measuring before the web font arrives would center the fallback font's
    // ink instead.
    (document.fonts?.load(font, text) ?? Promise.resolve()).then(measure, measure);
    return () => { cancelled = true; };
  }, [text]);
  return (
    <span className="hgw__quiz-glyphs" style={{ transform: `translateY(${offset}px)` }}>
      {text}
    </span>
  );
};

const MonumentPhoto: React.FC<{ photo: NonNullable<WordData['photo']>; alt: string }> = ({ photo, alt }) => {
  const { t } = useTranslation();
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoomed(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomed]);

  return (
    <figure className="hgw-photo">
      <button
        type="button"
        className="hgw-photo__zoom"
        onClick={(e) => { e.stopPropagation(); setZoomed(true); }}
        aria-label={t('hieroglyphCard.photoZoom')}
      >
        <img className="hgw-photo__img" src={photo.src} alt={alt} loading="lazy" decoding="async" />
      </button>
      <figcaption className="hgw-photo__credit">
        {t('hieroglyphCard.photoCredit')}{' '}
        <a href={photo.sourceUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
          {photo.credit}
        </a>
      </figcaption>
      {zoomed && ReactDOM.createPortal(
        <button
          type="button"
          className="hgw-photo-lightbox"
          onClick={() => setZoomed(false)}
          aria-label={t('hieroglyphCard.photoClose')}
        >
          <img src={photo.src} alt={alt} />
        </button>,
        document.body,
      )}
    </figure>
  );
};

const HieroglyphCard: React.FC<HieroglyphCardProps> = ({
  entryKey,
  word,
  gardinerSigns,
  crossReferences,
  onNavigate,
  onAnswered,
  example,
  guideStep,
  onGuideStepChange,
}) => {
  const { t, i18n } = useTranslation();
  // null = "not chosen yet" → falls back to the default tab below, so the
  // default can depend on which tabs this entry actually has.
  const [activeTab, setActiveTab] = useState<TabId | null>(null);
  const [activeSign, setActiveSign] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  const glyphChars = useMemo(() => Array.from(word.illustration ?? ''), [word.illustration]);
  const alignedDecomposition = gardinerSigns.length > 0 && glyphChars.length === gardinerSigns.length;
  // The "signe directeur" — the sign carrying the word's core phonetic value,
  // around which phonetic complements and the determinative are arranged.
  // Only meaningful when the illustration's glyphs line up 1:1 with the
  // parsed Gardiner codes; otherwise we can't know which glyph is which sign.
  const leadSignIndex = useMemo(() => {
    if (!word.leadSign || !alignedDecomposition) return -1;
    return gardinerSigns.findIndex((sign) => sign.code === word.leadSign);
  }, [gardinerSigns, word.leadSign, alignedDecomposition]);
  // A one-glyph illustration has nothing to decompose — that single glyph
  // *is* the signe directeur by construction, so it's hoverable too even
  // without a Gardiner-code match (e.g. iconic single-sign words like ꜥnḫ,
  // or entries whose codification doesn't parse into a clean chain).
  const effectiveLeadIndex = leadSignIndex >= 0 ? leadSignIndex : glyphChars.length === 1 ? 0 : -1;
  const readingText = useMemo(
    () => stripReadingPrefix(getWordReading(word, i18n.language)),
    [word, i18n.language],
  );
  const resolvedReferences = useMemo(
    () => crossReferences.filter((ref): ref is CrossReference & { word: WordData } => ref.word !== null),
    [crossReferences],
  );
  const photoTitle = word.photo
    ? (i18n.language === 'en' && word.photo.titleEn) || word.photo.title
    : '';
  const photoCaption = word.photo
    ? (i18n.language === 'en' && word.photo.captionEn) || word.photo.caption
    : '';
  const activeSignData = gardinerSigns[activeSign];
  const hasReveal = gardinerSigns.length > 0 || resolvedReferences.length > 0 || readingText.length > 0;

  // Default the decomposition's selected sign to the signe directeur whenever
  // the word changes, instead of always starting on the first sign.
  useEffect(() => {
    setActiveSign(leadSignIndex >= 0 ? leadSignIndex : 0);
    setSelectedOption(null);
    setActiveTab(null);
  }, [entryKey, leadSignIndex]);

  // Interactive elements below are real <button>s, and their children get
  // pointer-events:none (HieroglyphCard.css): the card lives inside a
  // react-pageflip page (KamaLearnMode.tsx), and the library's checkTarget()
  // only exempts a click from turning the page when the event *target's*
  // tagName is literally "a" or "button" — a stopPropagation() from a React
  // onClick fires too late, and a click landing on a <span> inside a button
  // would otherwise still flip the page.

  const decompositionPanel = gardinerSigns.length > 0 && (
    <div className="hgw__decomp">
      <div className="hgw__decomp-signs">
        {gardinerSigns.map((sign, i) => {
          const isLead = i === leadSignIndex;
          return (
            <div
              key={`${sign.code}-${i}`}
              className={`hgw__sign-col ${i === activeSign ? 'is-active' : ''} ${isLead ? 'is-lead' : ''}`}
            >
              {alignedDecomposition && (
                <span className="hgw__glyphs hgw__sign-glyph">{glyphChars[i]}</span>
              )}
              <span className="hgw__sign-connector" />
              <button
                type="button"
                className="hgw__sign-btn"
                onClick={(e) => { e.stopPropagation(); setActiveSign(i); }}
              >
                {sign.code}
              </button>
              {isLead && <span className="hgw__lead-badge">{t('hieroglyphCard.leadSignBadge')}</span>}
            </div>
          );
        })}
      </div>
      {activeSignData && (
        <p className="hgw__sign-detail">
          <strong>{activeSignData.code}</strong> — {activeSignData.categoryLabel}
        </p>
      )}
    </div>
  );

  const crossReferenceButtons = resolvedReferences.map((ref) => (
    <button
      key={`${ref.citedKey}-${ref.resolvedCategoryKey}`}
      type="button"
      className="hgw__xref"
      onClick={(e) => { e.stopPropagation(); onNavigate?.(ref); }}
    >
      <span className="hgw__glyphs hgw__xref-glyphs">{ref.word.illustration}</span>
      <span className="hgw__xref-meta">
        <span className="hgw__xref-key">{ref.citedKey}</span>
        <span className="hgw__xref-translation"><CopticText text={getWordTranslation(ref.word, i18n.language)} /></span>
        <span className="hgw__xref-cat">{ref.resolvedCategoryKey ? displayCategoryLabel(ref.resolvedCategoryKey, t) : ''}</span>
      </span>
    </button>
  ));

  // Quiz cards still reveal the full deep-dive (decomposition, analytical
  // reading, cross-references) inline once answered.
  const revealPanel = hasReveal && (
    <div className="hgw__reveal">
      {decompositionPanel}
      {readingText && (
        <div className="hgw__reading">
          <p className="hgw__reading-label">{t('hieroglyphCard.analyticalReading')}</p>
          <p className="hgw__reading-text"><CopticText text={readingText} /></p>
        </div>
      )}
      {crossReferenceButtons}
    </div>
  );

  if (word.guide) {
    return (
      <KamaGuideCard
        title={entryKey}
        guide={word.guide}
        step={guideStep}
        onStepChange={onGuideStepChange}
      />
    );
  }

  if (word.isSectionHeader) {
    // A section header has nothing to quiz or reveal-on-click — the whole
    // point is to be read at a glance, so it skips the stèle and its tabs
    // entirely and just shows everything inline.
    return (
      <div className="hgw hgw--header">
        <span className="hgw__glyphs hgw__header-glyphs">{word.illustration}</span>
        <p className="hgw__header-title">{entryKey}</p>
        <p className="hgw__header-subtitle"><CopticText text={getWordTranslation(word, i18n.language)} /></p>
        {readingText && <p className="hgw__header-note"><CopticText text={readingText} /></p>}
      </div>
    );
  }

  if (word.quiz) {
    const { question, options, correctIndex, explanation, optionsAreGlyphs } = getWordQuiz(word, i18n.language)!;
    const answered = selectedOption !== null;
    const isCorrect = selectedOption === correctIndex;

    return (
      <div className="hgw">
        <div className="hgw__card hgw__card--quiz" style={{ cursor: 'default' }}>
          {/* Chapter reviews all carry the same 𓏞 (scribe's palette) in
              their data — meaningless to a beginner and just pushes the four
              options down, the band above already names the chapter. */}
          {word.photo ? (
            <MonumentPhoto photo={word.photo} alt={photoTitle} />
          ) : !word.isChapterReview && (
            <span className="hgw__glyphs">{word.illustration}</span>
          )}
          <p className="hgw__quiz-question"><CopticText text={question} /></p>
          <div className="hgw__quiz-options">
            {options.map((option, i) => {
              let stateClass = '';
              if (answered && i === correctIndex) stateClass = 'is-correct';
              else if (answered && i === selectedOption) stateClass = 'is-incorrect';
              return (
                <button
                  key={i}
                  type="button"
                  className={`hgw__quiz-option ${optionsAreGlyphs ? 'hgw__quiz-option--glyphs' : ''} ${stateClass}`}
                  disabled={answered}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOption(i);
                    onAnswered?.(i === correctIndex);
                  }}
                >
                  {optionsAreGlyphs ? <CenteredGlyphs text={option} /> : <CopticText text={option} />}
                </button>
              );
            })}
          </div>
        </div>

        {answered && (
          <div className="hgw__reveal">
            {isCorrect ? (
              <IbisCheer seed={entryKey}>
                <CopticText text={explanation} />
              </IbisCheer>
            ) : (
              <div className="hgw__reading">
                <p className="hgw__reading-label is-incorrect">{t('kamaWordTest.notQuite')}</p>
                <p className="hgw__reading-text"><CopticText text={explanation} /></p>
              </div>
            )}
            {(entryKey || word.translation) && (
              <p className="hgw__quiz-answer">
                <span className="hgw__translit">{entryKey}</span>
                {entryKey && word.translation ? ' — ' : ''}
                <span className="hgw__translation"><CopticText text={getWordTranslation(word, i18n.language)} /></span>
              </p>
            )}
            {revealPanel}
          </div>
        )}
      </div>
    );
  }

  // Flashcard — design 19d « linteau »: a limestone stèle under a carved
  // lintel, a winged disc between two engraved rules, and the sign set in a
  // cartouche, flanked by the other signs of its chapter. Glyph, transliteration and translation are always visible; the
  // old "Détails" modal is replaced by tabs right under the stèle (son /
  // mot / origine), with a real example word shown first when there is one.
  const isSingleSign = glyphChars.length === 1;
  const [title, gloss] = splitTranslation(getWordTranslation(word, i18n.language));
  // Alphabet signs get the engraved "ALEPH · SE LIT « A »" label, with the
  // mnemonic sentence as the gloss — its "— lu comme un a" tail would only
  // repeat the label, so it's dropped.
  const alphabetSign = isSingleSign ? getAlphabetSign(entryKey, i18n.language) : null;

  const tabs: { id: TabId; label: string }[] = [];
  if (readingText) {
    tabs.push({ id: 'sound', label: isSingleSign ? t('hieroglyphCard.tabSound') : t('hieroglyphCard.tabReading') });
  }
  if (example || resolvedReferences.length > 0) {
    tabs.push({ id: 'word', label: example ? t('hieroglyphCard.tabWord') : t('hieroglyphCard.tabLinks') });
  }
  if (gardinerSigns.length > 0) {
    tabs.push({ id: 'origin', label: isSingleSign ? t('hieroglyphCard.tabOrigin') : t('hieroglyphCard.tabSigns') });
  }
  // The example word opens first when there is one (19d: "Un mot" is the
  // active tab — the example is meant to be visible straight away).
  const currentTab = tabs.find((tab) => tab.id === activeTab)?.id
    ?? (example ? 'word' : tabs[0]?.id);

  const exampleGlyphs = example ? Array.from(example.word.illustration ?? '') : [];

  return (
    <div className="hgw hgw--stele">
      <div className="hgw-stele-wrap">
        <span className="hgw-stele__lintel" aria-hidden="true" />
        <div className="hgw-stele">
          <span className="hgw-stele__disc" aria-hidden="true">
            <span className="hgw-stele__wing" />
            <span className="hgw-stele__sun" />
            <span className="hgw-stele__wing hgw-stele__wing--right" />
          </span>
          <span className="hgw-stele__rule" />

          {word.photo && (
            <div className="hgw-stele__row hgw-stele__row--photo">
              <MonumentPhoto photo={word.photo} alt={photoTitle} />
            </div>
          )}
          {!word.photo && (
          <div className="hgw-stele__row">
            <div className="hgw-stele__alcove">
              <span
                className={[
                  'hgw__glyphs',
                  'hgw-stele__glyphs',
                  isSingleSign ? 'hgw-stele__frame hgw-stele__frame--sign' : '',
                  !isSingleSign && word.cartouche ? 'hgw-stele__frame hgw-stele__frame--royal' : '',
                ].filter(Boolean).join(' ')}
              >
                {effectiveLeadIndex >= 0
                  ? glyphChars.map((char, i) =>
                      i === effectiveLeadIndex ? (
                        <span key={i} className="hgw__glyph-char is-lead hgw__lead-hover" tabIndex={0}>
                          {char}
                          {word.leadSignNote && (
                            <span className="hgw__lead-tooltip" role="tooltip">
                              <CopticText text={word.leadSignNote} />
                            </span>
                          )}
                        </span>
                      ) : (
                        <span key={i} className="hgw__glyph-char">
                          {char}
                        </span>
                      ),
                    )
                  : word.illustration}
              </span>
            </div>
          </div>
          )}

          <span className="hgw-stele__rule" />
          {word.photo ? (
            <>
              <p className="hgw-stele__label">
                <span className="hgw-stele__name">{photoTitle}</span>
              </p>
              <p className="hgw-stele__gloss">{photoCaption}</p>
            </>
          ) : alphabetSign ? (
            <>
              <p className="hgw-stele__label">
                <span className="hgw-stele__translit">{entryKey}</span>
                <span className="hgw-stele__reading">
                  {alphabetSign.name && `${alphabetSign.name} · `}
                  {t('hieroglyphCard.readAs', { sound: alphabetSign.sound })}
                </span>
              </p>
              <p className="hgw-stele__gloss hgw-stele__gloss--sentence"><CopticText text={title} /></p>
            </>
          ) : (
            <>
              <p className="hgw-stele__label">
                <span className="hgw-stele__translit">{entryKey}</span>
                <span className="hgw-stele__name"><CopticText text={title} /></span>
              </p>
              {gloss && <p className="hgw-stele__gloss"><CopticText text={gloss} /></p>}
            </>
          )}
        </div>
      </div>

      {tabs.length > 1 && (
        <div className="hgw-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={tab.id === currentTab}
              className={`hgw-tabs__tab${tab.id === currentTab ? ' is-active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setActiveTab(tab.id); }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {currentTab && (
        <div className="hgw-tabs__panel" role="tabpanel" key={currentTab}>
          {currentTab === 'word' && (example ? (
            <button
              type="button"
              className="hgw-example"
              onClick={(e) => { e.stopPropagation(); onNavigate?.(example); }}
            >
              <span className="hgw__glyphs hgw-example__glyphs">
                {exampleGlyphs.map((char, i) => (
                  <span key={i} className={isSingleSign && char === glyphChars[0] ? 'is-hl' : undefined}>{char}</span>
                ))}
              </span>
              <span className="hgw-example__meta">
                <span className="hgw-example__kicker">{t('hieroglyphCard.inAWord')}</span>
                <span className="hgw-example__translit">{highlightLetter(example.citedKey, entryKey)}</span>
                <span className="hgw-example__translation">
                  <CopticText text={splitTranslation(getWordTranslation(example.word, i18n.language))[0]} />
                </span>
              </span>
            </button>
          ) : crossReferenceButtons)}
          {currentTab === 'sound' && (
            <div className="hgw__reading">
              <p className="hgw__reading-text"><CopticText text={readingText} /></p>
            </div>
          )}
          {currentTab === 'origin' && decompositionPanel}
        </div>
      )}
    </div>
  );
};

export default HieroglyphCard;
